import React, { useState, useEffect, useRef } from"react";
import { useNavigate } from"react-router-dom";
import { useSubscription } from "../Context/SubscriptionContext";
import FeatureGuard from "./access/FeatureGuard";
import TutorShell, { RailSection, RailStat, RailTip } from "./aiTutor/TutorShell";
import { useTutorLanguage, LanguagePicker } from "./aiTutor/languages";
import {
  TutorMessages,
  TutorComposer,
  TutorBadge,
  TutorEmptyState,
} from "./aiTutor/TutorChat";
import API_BASE_URL from "../Config/config";
import NexusUsageBadge from "../NextGenBuilder/components/NexusUsageBadge";
import useNexusLearnerBilling from "../NextGenBuilder/hooks/useNexusLearnerBilling";

const InterviewPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 // Tenant / NextGen Builder mode bypasses the learner paywall — access is
 // already gated externally (NextGenPaidFeatureGuard for TSG Nexus).
 if (isTenantMode) {
   return <InterviewContent mode={mode} />;
 }

 return (
  <FeatureGuard feature="TSG_AI">
    <InterviewContent mode={mode} />
  </FeatureGuard>
 );
};

const InterviewContent = ({ mode = "learner" }) => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();
  const { langCode, langName, setLanguage } = useTutorLanguage({
    initialLanguage: undefined,
  });

 // TSG Nexus feature billing — inert outside /nextgen-builder/*.
 const nexus = useNexusLearnerBilling("AI_TUTOR", { subFeature: "INTERVIEW_PREP" });

 const handleAsk = async () => {
 if (!input.trim()) return;
 const currentInput = input;
 setChat((prev) => [...prev, { role: "user", content: currentInput }]);
 setInput("");
 setLoading(true);

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 // NextGen Builder accounts don't have `.userId`/`.user_id` — fall back to
 // the namespaced pseudo-identity written to localStorage by
 // NextGenAuthContext.jsx, same as ExplainPage.jsx already does.
 const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate(mode === "tenant" ? "/nextgen-builder/login" : "/login");
 return;
 }

 try {
 const res = await fetch(`${API_BASE_URL}/tsg/interview_qa`, {
 method:"POST",
 headers: {"Content-Type":"application/json", ...nexus.headers() },
 body: JSON.stringify({
 userId,
 prompt: currentInput,
 chatHistory: chat,
 // Full language NAME ("Gujarati"), not the code ("gu") — the backend
 // builds its prompts from this value, and a bare code was ignored by
 // the model, so answers always came back in English.
 language: langName || langCode,
 // Empty, NOT "general": the backend treats a non-empty topic as the
 // question domain, so "general" forced every MCQ to contain the word
 // "general" instead of following the student's actual prompt.
 technology:"",
 topic:"",
 }),
 });

 const data = await res.json();

 if (res.status === 402 && nexus.handleQuota(data)) {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"You've used all your free Interview Prep prompts for this month. Recharge to continue." }
 ]);
 return;
 }

 nexus.noteUsage(res);

 const reply = data.reply || (data.error ? `⚠️ ${data.error}` : "No reply — please try again.");
 setChat((prev) => [
 ...prev,
 { role:"assistant", content: reply }
 ]);
 } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." }
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  const STARTERS = [
    "Ask me common React interview questions",
    "Give me a system design question for a junior role",
    "Practice behavioural questions about teamwork",
    "Quiz me on JavaScript closures and scope",
  ];

  return (
    <TutorShell
      language={<LanguagePicker value={langCode} onChange={setLanguage} />}
      icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></svg>}
      title="Interview Prep"
      subtitle="Practise real interview questions and get feedback on your answers, one question at a time."
      onBack={() => navigate(-1)}
      headerExtra={nexus.isNexus ? <NexusUsageBadge feature="AI_TUTOR" subFeature="INTERVIEW_PREP" /> : null}
      badges={<TutorBadge label="Mode" value="general" />}
      rail={
        <>
          <RailSection title="This session">
            <RailStat label="Questions covered" value={String(Math.ceil(chat.length / 2))} />
            <RailStat label="Focus" value="General" hint="Ask for a specific role or stack to narrow it." />
          </RailSection>
          <RailSection title="Start with">
            {STARTERS.map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="w-full text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 mb-2 last:mb-0 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
              >
                <span className="text-[12px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
              </button>
            ))}
          </RailSection>
          <RailSection title="Practice well">
            <RailTip>Answer in full before asking for feedback — you get a far more useful critique.</RailTip>
            <RailTip>Name the role you are targeting and the questions adjust to that level.</RailTip>
          </RailSection>
        </>
      }
      composer={
        <TutorComposer
          value={input}
          onChange={setInput}
          onSubmit={handleAsk}
          loading={loading}
          disabled={!input.trim()}
          placeholder="Enter topic for interview prep..."
          hint={nexus.hint}
        />
      }
    >
      <TutorMessages
        chat={chat}
        chatEndRef={chatEndRef}
        empty={
          <TutorEmptyState
            icon={<svg className="w-9 h-9 text-[#5B21B6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></svg>}
            title="Ready when you are"
            description="Pick a topic or a role and I will ask interview questions, then critique your answers."
          >
            <div className="grid sm:grid-cols-2 gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
                >
                  <span className="text-[13px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
                </button>
              ))}
            </div>
          </TutorEmptyState>
        }
      />
    </TutorShell>
  );
};

export default InterviewPage;



